/**
 * Error Interpreter - Converts technical backend errors into user-friendly messages
 * This can be easily updated as new error patterns are discovered
 */

// Error patterns and their user-friendly translations
const ERROR_PATTERNS = [
  // Audio/Upload related errors
  {
    pattern: /download error|unable to download/i,
    message: "Audio file could not be accessed. Please try recording again."
  },
  {
    pattern: /audio.*upload.*failed|segment.*upload.*failed/i,
    message: "Audio upload failed. Please check your connection and try again."
  },
  {
    pattern: /no valid audio data|file_size.*0/i,
    message: "No audio was recorded. Please ensure your microphone is working and try again."
  },
  {
    pattern: /microphone|mic.*permission/i,
    message: "Microphone access is required. Please enable microphone permissions and try again."
  },

  // Transcription service errors
  {
    pattern: /assemblyai|transcription.*error/i,
    message: "Audio transcription service is temporarily unavailable. Please try again later."
  },
  {
    pattern: /transcription.*timeout/i,
    message: "Audio processing is taking longer than expected. Please try again."
  },
  {
    pattern: /transcription.*failed/i,
    message: "Audio transcription failed. Please try again or contact support if the issue persists."
  },

  // AI/OpenAI related errors
  {
    pattern: /openai|gpt-|ai.*model/i,
    message: "Note generation service is temporarily unavailable. Please try again later."
  },
  {
    pattern: /note.*generation.*failed/i,
    message: "Visit note generation failed. Please try again or generate manually."
  },
  {
    pattern: /rate.*limit|quota.*exceeded/i,
    message: "Service is currently busy. Please wait a moment and try again."
  },

  // Network and authentication errors
  {
    pattern: /network.*error|connection.*error/i,
    message: "Connection error. Please check your internet connection and try again."
  },
  {
    pattern: /authentication.*failed|unauthorized|401/i,
    message: "Authentication failed. Please sign in again."
  },
  {
    pattern: /token.*expired|invalid.*token/i,
    message: "Your session has expired. Please sign in again."
  },
  {
    pattern: /forbidden|403/i,
    message: "You don't have permission to perform this action."
  },

  // Server and database errors
  {
    pattern: /internal.*server.*error|500/i,
    message: "A server error occurred. Please try again or contact support if the issue persists."
  },
  {
    pattern: /database.*error|db.*error/i,
    message: "A database error occurred. Please try again later."
  },
  {
    pattern: /not.*found|404/i,
    message: "The requested resource was not found."
  },

  // WebSocket and real-time errors
  {
    pattern: /websocket|connection.*lost|reconnect/i,
    message: "Connection lost. Attempting to reconnect..."
  },
  {
    pattern: /session.*expired|session.*timeout/i,
    message: "Your session has expired. Please refresh and try again."
  },

  // File and storage errors
  {
    pattern: /s3.*error|storage.*error|aws.*error/i,
    message: "File storage error. Please try again later."
  },
  {
    pattern: /file.*not.*found|missing.*file/i,
    message: "Audio file not found. Please record again."
  },
  {
    pattern: /file.*too.*large|size.*exceeded/i,
    message: "File size is too large. Please try a shorter recording."
  },

  // Timeout and processing errors
  {
    pattern: /timeout|timed.*out/i,
    message: "Request timed out. Please try again."
  },
  {
    pattern: /processing.*failed|job.*failed/i,
    message: "Processing failed. Please try again or contact support."
  },

  // Generic validation errors
  {
    pattern: /validation.*error|invalid.*input/i,
    message: "Invalid input. Please check your data and try again."
  },
  {
    pattern: /missing.*required|required.*field/i,
    message: "Required information is missing. Please complete all fields."
  }
];

/**
 * Interprets a technical error message and returns a user-friendly version
 * @param {string|Error} error - The error message or Error object
 * @returns {string} User-friendly error message
 */
export function interpretError(error) {
  if (!error) return "An unknown error occurred.";
  
  // Extract error message string
  const errorMessage = typeof error === 'string' ? error : 
                      error.message || error.detail || error.error || 
                      JSON.stringify(error);
  
  // Check each pattern for a match
  for (const { pattern, message } of ERROR_PATTERNS) {
    if (pattern.test(errorMessage)) {
      return message;
    }
  }
  
  // Fallback: if no pattern matches, return a generic message
  // Log the original error for developers to add new patterns
  console.warn('[ErrorInterpreter] Unhandled error pattern:', errorMessage);
  return "Something went wrong. Please try again or contact support if the issue persists.";
}

/**
 * Interprets an API response error and returns user-friendly message
 * @param {Response|Object} response - Fetch response or error object
 * @returns {Promise<string>} User-friendly error message
 */
export async function interpretApiError(response) {
  if (!response) return interpretError(null);
  
  try {
    // If it's a Response object, extract the error details
    if (response.json && typeof response.json === 'function') {
      const errorData = await response.json();
      return interpretError(errorData);
    }
    
    // If it's already an error object
    return interpretError(response);
  } catch (e) {
    // If JSON parsing fails, use the response status
    if (response.status) {
      return interpretError(`HTTP ${response.status} error`);
    }
    return interpretError("Network error occurred");
  }
}

/**
 * Creates a standardized error object for UI components
 * @param {string|Error} error - The error to interpret
 * @param {string} [context] - Additional context about where the error occurred
 * @returns {Object} Standardized error object
 */
export function createUserError(error, context = '') {
  const userMessage = interpretError(error);
  const technicalMessage = typeof error === 'string' ? error : 
                          error.message || error.detail || JSON.stringify(error);
  
  return {
    message: userMessage,
    context: context,
    technical: technicalMessage,  // For debugging/logging
    timestamp: new Date().toISOString()
  };
}

/**
 * Logs technical error details while showing user-friendly message
 * @param {string|Error} error - The error to log and interpret
 * @param {string} [context] - Additional context
 * @returns {string} User-friendly message
 */
export function logAndInterpretError(error, context = '') {
  const userError = createUserError(error, context);
  
  // Log technical details for developers
  console.error(`[${context || 'ErrorInterpreter'}]`, {
    userMessage: userError.message,
    technicalError: userError.technical,
    timestamp: userError.timestamp,
    context: context
  });
  
  return userError.message;
}

export default {
  interpretError,
  interpretApiError,
  createUserError,
  logAndInterpretError
};